import React, { useEffect, useState } from 'react';
import { FmlxBanner, FmlxButton, FmlxTextBox } from 'fmlx-common-ui';
import { ARCHIVING_NAME_MAX_LENGTH } from './constants';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import {
  ARCHIVING_NAME_VALIDATION_MESSAGES,
  ArchivingNameValidationError,
  formatArchivingScheduleTime,
  getArchivingNameValidationError,
} from './utils';
import ArchivingService from 'business/services/ArchivingService';
import { ResponseType, TimePropertyDTO } from 'business/models/Models';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { PropertyConstants } from 'enums/PropertyConstants';

type ArchiveExperimentModalBodyProps = {
  experimentCount: number;
  onClose: () => void;
  onRunArchiving: (archivingName: string) => void;
};

export const ArchiveExperimentModalBody = ({
  experimentCount,
  onClose,
  onRunArchiving,
}: ArchiveExperimentModalBodyProps) => {
  const [archivingName, setArchivingName] = useState('');
  const [validationError, setValidationError] =
    useState<ArchivingNameValidationError | null>(null);
  const [archivingNames, setArchivingNames] = useState<string[]>([]);
  const scheduledStartTime = useSystemProperty<TimePropertyDTO>(
    PropertyConstants.ArchivingStartTime,
    '',
    { refreshOnUpdate: true, forceRefreshOnMount: true },
  );
  const scheduledEndTime = useSystemProperty<TimePropertyDTO>('Archiving.EndTime', '');

  useEffect(() => {
    let isMounted = true;

    const fetchArchivingNames = async () => {
      try {
        const archivingService = new ArchivingService();
        const result = await archivingService.fetchArchivingJobNames();
        if (isMounted && result.responseType === ResponseType.Success) {
          setArchivingNames(result.value ?? []);
        }
      } catch (error) {
        console.error('Failed to fetch archiving job names', error);
      }
    };

    fetchArchivingNames();

    return () => {
      isMounted = false;
    };
  }, []);

  const experimentLabel = experimentCount === 1 ? 'experiment' : 'experiments';

  const currentTimeString = new Date().toTimeString().slice(0, 8);
  const isWithinArchivingWindow =
    scheduledStartTime <= scheduledEndTime
      ? currentTimeString >= scheduledStartTime && currentTimeString <= scheduledEndTime
      : currentTimeString >= scheduledStartTime || currentTimeString <= scheduledEndTime;
  const experimentCountString = `${experimentCount} ${experimentLabel} will remove from explorer and database.`;
  const archivingStartTimeText = isWithinArchivingWindow
    ? 'Archiving process will start immediately'
    : `Archiving process will start around ${formatArchivingScheduleTime(
        scheduledStartTime,
      )}`;
  const bannerTitle = `${experimentCountString} ${archivingStartTimeText}`;

  const handleRunArchiving = () => {
    const error = getArchivingNameValidationError(archivingName, archivingNames);
    if (error) {
      setValidationError(error);
      return;
    }

    onRunArchiving(archivingName.trim());
  };

  const handleNameChange = (value: string) => {
    setArchivingName(value);
    if (validationError !== null) {
      setValidationError(getArchivingNameValidationError(value, archivingNames));
    }
  };

  const validationMessage = validationError
    ? ARCHIVING_NAME_VALIDATION_MESSAGES[validationError]
    : '';

  return (
    <div id="archive-experiment-modal-body" className="flex flex-col gap-4">
      <FmlxBanner
        id="archive-experiment-warning-banner"
        variant={FmlxBanner.Variant.WARNING}
        title={bannerTitle}
        subtitle=""
        hideCloseButton
        show
      />

      <div id="archive-experiment-name-field" className="flex flex-col gap-1">
        <label
          id="archive-experiment-name-label"
          htmlFor="archive-experiment-name-input"
          className="text-sm text-gray-800"
        >
          Please enter archiving name <span className="text-[#DB0000]">*</span>
        </label>
        <div>
          <FmlxTextBox
            id="archive-experiment-name-input"
            value={archivingName}
            onChange={(event) => handleNameChange(event.value)}
            maxLength={ARCHIVING_NAME_MAX_LENGTH}
            variant={FmlxTextBox.Variant.OUTLINE}
          />
        </div>
        <div
          id="archive-experiment-name-error"
          className={`flex min-h-10 items-start gap-1 text-sm text-[#DB0000] ${
            validationError ? '' : 'invisible'
          }`}
          aria-hidden={!validationError}
        >
          <RMSvgIcon
            id="archive-experiment-name-error-icon"
            name="Error"
            fill="#DB0000"
            className="mt-0.5 h-4 w-4 shrink-0"
          />
          <span>{validationMessage}</span>
        </div>
        <div
          id="archive-experiment-name-counter"
          className="text-right text-xs text-gray-500"
        >
          {archivingName.length}/{ARCHIVING_NAME_MAX_LENGTH}
        </div>
      </div>

      <div
        id="archive-experiment-modal-footer"
        className="flex flex-row items-center justify-end w-full h-12 gap-3 px-2 mt-2 border-t border-t-[#CACACA] border-solid border-0"
      >
        <FmlxButton
          id="archive-experiment-close-button"
          type={FmlxButton.Type.PRIMARY}
          variant={FmlxButton.Variant.OUTLINE}
          label="CLOSE"
          onClick={onClose}
        />
        <FmlxButton
          id="archive-experiment-run-button"
          type={FmlxButton.Type.PRIMARY}
          variant={FmlxButton.Variant.CONTAIN}
          label="RUN ARCHIVING"
          onClick={handleRunArchiving}
        />
      </div>
    </div>
  );
};
